import { Link } from 'react-router'
import { cn } from '#app/utils/misc.tsx'

type Variant = 'full' | 'compact'

type Props = {
	eyebrow?: string
	title: React.ReactNode
	intro?: React.ReactNode
	/** Slot on the right column (full variant only). */
	accessory?: React.ReactNode
	backTo?: string
	backLabel?: string
	variant?: Variant
	className?: string
}

/**
 * Page-top hero. Identity only — title, intro, optional accessory. Navigation
 * is the SiteNav's job (jumping between sections) and the SiblingNav's job
 * (jumping within a section).
 *
 * - `full` (default): asymmetric 12-col layout. Title on the left, intro +
 *   optional accessory on the right.
 * - `compact`: single-column, smaller padding. Used on sub-pages.
 */
export function PageHero({
	eyebrow,
	title,
	intro,
	accessory,
	backTo,
	backLabel,
	variant = 'full',
	className,
}: Props) {
	const back = backTo ? (
		<Link
			to={backTo}
			className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
		>
			<span aria-hidden>←</span> {backLabel ?? 'Takaisin'}
		</Link>
	) : null

	if (variant === 'compact') {
		return (
			<section className={cn('border-border border-b', className)}>
				<div className="container py-8 md:py-12">
					{back ? <div className="mb-3">{back}</div> : null}
					{eyebrow ? (
						<div className="text-muted-foreground font-mono text-xs font-semibold tracking-widest uppercase">
							{eyebrow}
						</div>
					) : null}
					<h1 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">
						{title}
					</h1>
					{intro ? (
						<p className="text-foreground/80 mt-3 max-w-2xl text-base leading-relaxed">
							{intro}
						</p>
					) : null}
				</div>
			</section>
		)
	}

	return (
		<section className={cn('border-border border-b', className)}>
			<div className="container py-12 md:py-20">
				{back ? <div className="mb-6">{back}</div> : null}
				<div className="grid gap-10 md:grid-cols-12 md:gap-12">
					<div className="md:col-span-7">
						{eyebrow ? (
							<div className="text-muted-foreground font-mono text-xs font-semibold tracking-[0.3em] uppercase">
								{eyebrow}
							</div>
						) : null}
						<h1 className="mt-3 text-4xl leading-[1.05] font-bold tracking-tight md:text-6xl">
							{title}
						</h1>
					</div>
					{intro || accessory ? (
						<aside className="md:col-span-5 md:pt-4">
							{intro ? (
								<div className="text-foreground/80 text-base leading-relaxed md:text-lg">
									{intro}
								</div>
							) : null}
							{accessory ? <div className="mt-6">{accessory}</div> : null}
						</aside>
					) : null}
				</div>
			</div>
		</section>
	)
}
